/**
 * Os ícones do KairósHub.
 *
 * **Desenhados aqui, não trazidos de uma biblioteca.** Dois motivos, e os dois
 * estão no `CLAUDE.md`: a política de segurança do servidor é `default-src
 * 'self'` e bloquearia o pedido; e buscar arquivo fora entrega o IP de quem
 * abriu a tela — numa empresa que investiga patrimônio, isso não se faz.
 *
 * Traço, não preenchimento: herdam a cor do texto por `currentColor`, então
 * acompanham sozinhos o tema claro, o escuro e o estado da aba. Uma versão
 * preenchida exigiria uma cor própria, que viraria hexadecimal inventado.
 *
 * ⚠️ **Cada ícone desenha o que a aba É, não como ela se apresenta.** Um
 * ícone de calendário para "Comercial" descreveria a tela; o funil descreve o
 * trabalho. Quando os dois brigam, ganha o trabalho.
 */

export type NomeIcone =
  | 'hoje'
  | 'calendario'
  | 'clientes'
  | 'comercial'
  | 'financeiro'
  | 'relatorios'
  | 'marketing';

/**
 * Ação, não destino.
 *
 * Fica num tipo separado de propósito: assim nenhuma lupa acaba virando aba por
 * engano quando alguém acrescentar um item à navegação.
 */
export type NomeAcao =
  | 'lupa'
  | 'atencao'
  /* ⭐ Os do sistema visual 2026 (14/09/2026): dados da ficha e ações de
     cartão. Todos em traço e `currentColor`, no mesmo desenho dos de cima. */
  | 'proposta'
  | 'contrato'
  | 'concluido'
  | 'cancelado'
  | 'caso'
  | 'copiar'
  | 'lugar'
  | 'relogio'
  | 'pessoa'
  | 'email'
  | 'telefone'
  | 'indicou'
  | 'globo'
  | 'assina'
  | 'lapis'
  | 'lixeira'
  | 'mais'
  | 'fundo'
  | 'cobranca'
  | 'seta-fora'
  | 'check'
  /* ⭐ Linha rápida (18/09/2026): subir um PDF — o par de `baixar`. */
  | 'subir'
  /* ⭐ Parte 3 (14/09/2026): a saída em apresentação, no topo do relatório. */
  | 'tela'
  /* ⭐ Parte 4 (14/09/2026): a linha da lista que leva à ficha. */
  | 'avancar'
  /* ⭐ O menu que se recolhe (14/09/2026): o painel com a seta para dentro
     recolhe; com a seta para fora, fixa aberto. */
  | 'menu-recolher'
  | 'menu-fixar'
  /* ⭐ O link de cadastro (16/09/2026): o elo e a caixa de entrada. */
  | 'elo'
  | 'caixa'
  | 'voltar'
  | 'fechar'
  /* ⭐ O pedido de pesquisa (17/09/2026): trazer do pedido para o caso. */
  | 'trazer'
  /* ⭐ Os documentos do pedido (18/09/2026): abrir a cópia, baixar o original
     (com confirmação), e o cadeado do rodapé que diz o que se abre. */
  | 'olho'
  | 'baixar'
  | 'cadeado'
  /* ⭐ O calendário editorial (15/10/2026, PRD §50): em que rede o post sai. */
  | 'instagram'
  | 'linkedin'
  /* ⭐ O conteúdo do post (15/10/2026, PRD §50.5): subir e descer o slide na
     ordem. ⚠️ Não são `subir` e `baixar`, que têm o chão do arquivo: aqueles
     enviam e recebem; estes só mudam de lugar. */
  | 'acima'
  | 'abaixo';

const CAMINHOS: Record<NomeIcone | NomeAcao, string[]> = {
  /* Uma casa: é o início do Hub, o lugar para onde o logotipo sempre levou.
     Ganhou ícone quando "Hoje" passou a ter nome no menu lateral (14/09/2026). */
  hoje: ['M3 11.5 12 4l9 7.5', 'M5.5 9.8V20h13V9.8', 'M10 20v-5.5h4V20'],
  /* Grade do mês com o dia marcado. A aba é prazo e reunião: o que importa é
     que existe um dia assinalado, não que existe um calendário. */
  calendario: [
    'M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
    'M4 10h16',
    'M8 3v4',
    'M16 3v4',
    'M9 14.5h2v2H9z',
  ],
  /* Duas pessoas: o cliente da KAIRÓS é escritório, e escritório tem mais de
     uma pessoa com quem se fala. Uma silhueta sozinha diria "usuário". */
  clientes: [
    'M2.5 20v-1.2A4.3 4.3 0 0 1 6.8 14.5h3A4.3 4.3 0 0 1 14 18.8V20',
    'M8.3 11.5a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2',
    'M17 20v-1.2a4.3 4.3 0 0 0-2.6-3.95',
    'M14.8 4.6a3.6 3.6 0 0 1 0 6.9',
  ],
  /* Funil: muitos entram no topo, poucos saem assinados embaixo. É a única
     figura que já explica o que a aba faz sem legenda. */
  comercial: ['M3.5 5h17l-6.4 7.4V19l-4.2 2.2v-8.8z'],
  /* Cédula. Simples de propósito: o Financeiro só lê contrato e parcela, não
     é painel de indicador — o ícone não deve prometer gráfico. */
  financeiro: [
    'M2.5 7.5a1 1 0 0 1 1-1h17a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1z',
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
    'M6 12h.01',
    'M18 12h.01',
  ],
  /* Documento com a ponta dobrada. A KAIRÓS entrega documento assinado — o
     ícone é literalmente o produto. */
  relatorios: [
    'M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z',
    'M14 3.5V8h4.5',
    'M9 13h6',
    'M9 16.5h4',
  ],
  /* Lâmpada: a aba é laboratório de ideia e roteiro, não disparo de campanha.
     Um megafone diria "anunciar", que é o que a KAIRÓS justamente não faz. */
  marketing: [
    'M12 3.2a5.8 5.8 0 0 0-3.9 10.1c.62.68 1 1.5 1.05 2.45h5.7c.05-.95.43-1.77 1.05-2.45A5.8 5.8 0 0 0 12 3.2z',
    'M9.6 19h4.8',
    'M10.6 21.5h2.8',
  ],


  /* Lupa. Cabo curto e inclinado, para não competir com o olho ao lado. */
  lupa: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.35-4.35'],

  /*
   * ⭐ **A plaquinha de atenção — 08/09/2026, a pedido dele:** *"ao invés de
   * marcar com 'em branco', pode marcar, como por exemplo, uma plaquinha de
   * atenção, de forma sutil e simples"*.
   *
   * Triângulo de cantos arredondados, com a barra e o ponto. ⚠️ **Sem
   * preenchimento**, como todos os ícones da casa — é traço e `currentColor`, e
   * é isso que o deixa discreto o bastante para ficar ao lado de um nome de
   * tópico sem virar alarme.
   */
  atencao: [
    'M10.6 4.1 2.9 17.4a1.6 1.6 0 0 0 1.4 2.4h15.4a1.6 1.6 0 0 0 1.4-2.4L13.4 4.1a1.6 1.6 0 0 0-2.8 0z',
    'M12 9.5v3.8',
    'M12 16.6h.01',
  ],

  /* ── Sistema visual 2026 ─────────────────────────────────────────────── */
  proposta: ['M6 3.5h12v17H6z', 'M9 8h6', 'M9 11.5h6', 'M9 15h3'],
  contrato: ['M6 3.5h8.5L19 8v12.5H6z', 'M14 3.5V8h5', 'M9 12.5h7', 'M9 16h5'],
  concluido: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'm8 12.3 2.8 2.7L16.5 9.5'],
  cancelado: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'm8.5 8.5 7 7'],
  caso: ['M3.5 7.5h17v12h-17z', 'M9 7.5V5h6v2.5', 'M3.5 12.5h17'],
  copiar: ['M9 9h10v11H9z', 'M5 15V4h10'],
  lugar: ['M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z', 'M12 12.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4'],
  relogio: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M12 7.5V12l3 2'],
  pessoa: ['M5 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1', 'M12 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7'],
  email: ['M3.5 6.5h17v11h-17z', 'M3.5 7l8.5 6.5L20.5 7'],
  telefone: ['M5 3.5h3.5l1.5 4.5-2.2 1.4a11 11 0 0 0 6.8 6.8L16 14l4.5 1.5V19a1.5 1.5 0 0 1-1.6 1.5A16.5 16.5 0 0 1 3.5 5.1 1.5 1.5 0 0 1 5 3.5z'],
  indicou: ['M15 5.5h4.5V10', 'M19.5 5.5 11 14', 'M18 13.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H11'],
  globo: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M3 12h18', 'M12 3a14 14 0 0 1 0 18', 'M12 3a14 14 0 0 0 0 18'],
  assina: ['M3 19c3-1 4.5-4 6-8s3-6 5-6 1.5 3-1 6-4 5-2 6 4-2 6-2 2 2 4 2'],
  lapis: ['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4'],
  lixeira: ['M4 7h16', 'M9 7V4.5h6V7', 'M6.5 7l1 13h9l1-13', 'M10 11v6', 'M14 11v6'],
  mais: ['M12 5v14', 'M5 12h14'],
  fundo: ['M4 8.5 12 4l8 4.5-8 4.5z', 'M4 12.5l8 4.5 8-4.5', 'M4 16.5 12 21l8-4.5'],
  cobranca: ['M3 7h18v10H3z', 'M3 11h18', 'M7 15h3'],
  'seta-fora': ['M8 16 16 8', 'M9 8h7v7'],
  check: ['M5 12.5 10 17 19 7.5'],
  /* Uma tela sobre o pé: a apresentação é para a reunião por vídeo. */
  tela: ['M3.5 5h17v11h-17z', 'M9 20h6', 'M12 16v4'],
  /* Chevron: ir adiante, dentro do Hub — a seta inclinada diz "sai daqui". */
  avancar: ['m9.5 6 6 6-6 6'],
  'menu-recolher': ['M4 4.5h16v15H4z', 'M9.5 4.5v15', 'm16 9.5-2.5 2.5 2.5 2.5'],
  'menu-fixar': ['M4 4.5h16v15H4z', 'M9.5 4.5v15', 'm13.5 9.5 2.5 2.5-2.5 2.5'],
  elo: ['M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1', 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1'],
  caixa: ['M4 13h4l2 3h4l2-3h4', 'M4 13 7 5h10l3 8v6H4z'],
  trazer: ['M12 4v11', 'm7 10 5 5 5-5', 'M5 20h14'],
  voltar: ['m14.5 6-6 6 6 6'],
  fechar: ['M6 6l12 12', 'M18 6 6 18'],
  olho: ['M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6'],
  baixar: ['M12 4v10', 'm8 11 4 4 4-4', 'M5 19h14'],
  subir: ['M12 15V5', 'm8 8.5 4-4 4 4', 'M5 19h14'],
  acima: ['m6 14.5 6-6 6 6'],
  abaixo: ['m6 9.5 6 6 6-6'],
  cadeado: ['M6 11h12v9H6z', 'M8.5 11V8a3.5 3.5 0 0 1 7 0v3'],
  /*
   * ⭐ **As duas redes — 15/10/2026.** Desenhadas aqui, em traço e
   * `currentColor`, como todo ícone da casa: a marca de cada rede é que diz
   * onde o post sai, e é lida antes do nome. ⛔ Sem a cor de cada rede: na
   * grade, a cor é da situação do post, e duas cores no mesmo cartão brigariam.
   */
  instagram: [
    'M7.5 3.5h9a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9a4 4 0 0 1-4-4v-9a4 4 0 0 1 4-4z',
    'M12 15.6a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2',
    'M17 7h.01',
  ],
  linkedin: [
    'M5.5 3.5h13a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2z',
    'M8 10.5V16.5',
    'M8 7.5h.01',
    'M11.5 16.5v-6',
    'M11.5 13a2.5 2.5 0 0 1 5 0v3.5',
  ],
};

export function Icone({
  nome,
  tamanho = 17,
}: {
  nome: NomeIcone | NomeAcao;
  tamanho?: number;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {CAMINHOS[nome].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
